/**
 * Is this native build too old to keep using?
 *
 * The website deploys on every merge, but the App Store and Play builds ship
 * by hand (android-play.yml and ios-testflight.yml are manual), so a member's
 * phone can run code months behind the site. In November 2026 that is exactly
 * what happened: an Android build from before 8 September had no step list,
 * no in-session flow view and still said "edit booking", while the web app
 * showed a checked-in Nordic flow correctly — the two were different code.
 *
 * The bundle carries the moment it was built (vite.config.ts `define`), and
 * the site publishes the oldest build time it still accepts
 * (public/app-min-build.json). Older → the update screen, newer → nothing.
 * Every failure (offline, no policy, unparsable dates) is fail-open: a member
 * at the door must never be locked out of their timer by a missing JSON file.
 */

export const APP_MIN_BUILD_URL = "https://zuzuforyou.com/app-min-build.json";

/** When this bundle was built, or null outside a Vite build (tests). */
export function appBuildTime(): string | null {
  return typeof __ZZ_BUILD_TIME__ === "string" ? __ZZ_BUILD_TIME__ : null;
}

/** True only when both dates parse and the build is strictly older. */
export function isBuildTooOld(
  buildTime: string | null | undefined,
  minBuildTime: string | null | undefined,
): boolean {
  if (!buildTime || !minBuildTime) return false;
  const built = Date.parse(buildTime);
  const min = Date.parse(minBuildTime);
  if (Number.isNaN(built) || Number.isNaN(min)) return false;
  return built < min;
}

export type AppMinBuildPolicy = { minBuildTime: string };

/** Fetch the published policy; null on any failure. */
export async function fetchAppMinBuild(
  fetchImpl: typeof fetch = fetch,
  url: string = APP_MIN_BUILD_URL,
): Promise<AppMinBuildPolicy | null> {
  try {
    const res = await fetchImpl(url, { cache: "no-store" });
    if (!res.ok) return null;
    const body = (await res.json()) as { minBuildTime?: unknown };
    return typeof body?.minBuildTime === "string"
      ? { minBuildTime: body.minBuildTime }
      : null;
  } catch {
    return null;
  }
}

/** One call for the gate: does this build need updating right now? */
export async function appNeedsUpdate(
  fetchImpl: typeof fetch = fetch,
  buildTime: string | null = appBuildTime(),
): Promise<boolean> {
  if (!buildTime) return false;
  const policy = await fetchAppMinBuild(fetchImpl);
  return isBuildTooOld(buildTime, policy?.minBuildTime);
}
